import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BackendStorageService } from './backend-storage.service';

export interface SpotifyUser {
  id: string;
  display_name: string;
  email: string;
  images: Array<{
    url: string;
    height: number;
    width: number;
  }>;
  followers: {
    total: number;
  };
  country: string;
  product: string; // "premium" or "free"
}

export interface UserPreferences {
  autoRemoveDuplicates?: boolean;
  voiceControlsEnabled?: boolean;
  defaultPlaybackDevice?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private userSubject = new BehaviorSubject<SpotifyUser | null>(null);
  public user$ = this.userSubject.asObservable();

  private preferencesSubject = new BehaviorSubject<UserPreferences>(this.getDefaultPreferences());
  public preferences$ = this.preferencesSubject.asObservable();
  private initialized = false;

  constructor(
    private http: HttpClient,
    private backendStorage: BackendStorageService
  ) {
    this.loadPreferences();
  }

  /**
   * Get user profile from Spotify API
   */
  getUserProfile(): Observable<SpotifyUser> {
    return this.http.get<SpotifyUser>(`${environment.apiBaseUrl}/spotify/me`, {
      withCredentials: true
    }).pipe(
      tap(user => {
        this.userSubject.next(user);
      })
    );
  }

  /**
   * Get current user profile from cache
   */
  getCurrentUser(): SpotifyUser | null {
    return this.userSubject.value;
  }

  /**
   * Update user preferences
   */
  updatePreferences(preferences: Partial<UserPreferences>): void {
    const currentPreferences = this.preferencesSubject.value;
    const updatedPreferences = { ...currentPreferences, ...preferences };
    
    this.preferencesSubject.next(updatedPreferences);
    
    if (this.initialized) {
      this.backendStorage.updateUserPreferences(preferences).subscribe({
        error: (error) => {
          console.error('Failed to save preferences to backend:', error);
          // Fallback to localStorage on backend failure
          this.savePreferencesToLocalStorage(updatedPreferences);
        }
      });
    } else {
      this.savePreferencesToLocalStorage(updatedPreferences);
    }
  }

  /**
   * Get current user preferences
   */
  getPreferences(): UserPreferences {
    return this.preferencesSubject.value;
  }

  /**
   * Check if user has Spotify Premium
   */
  isPremiumUser(): boolean {
    const user = this.getCurrentUser();
    return user?.product === 'premium';
  }

  /**
   * Get user's profile image URL
   */
  getUserImageUrl(): string | null {
    const user = this.getCurrentUser();
    if (user && user.images && user.images.length > 0) {
      // Return the smallest image for profile display
      return user.images[user.images.length - 1].url;
    }
    return null;
  }

  /**
   * Clear user data (for logout)
   */
  clearUserData(): void {
    this.userSubject.next(null);
  }

  /**
   * Load preferences from backend or localStorage
   */
  private async loadPreferences(): Promise<void> {
    try {
      // Try to load from backend storage
      const backendPrefs = await this.backendStorage.getUserPreferences().toPromise();
      if (backendPrefs) {
        this.preferencesSubject.next({ ...this.getDefaultPreferences(), ...backendPrefs });
        this.initialized = true;
        return;
      }
    } catch (error) {
      console.warn('Failed to load preferences from backend, trying localStorage:', error);
    }

    // Fallback to localStorage for migration or offline scenarios
    try {
      const storedPreferences = localStorage.getItem('userPreferences');
      if (storedPreferences) {
        const preferences = JSON.parse(storedPreferences);
        const combinedPrefs = { ...this.getDefaultPreferences(), ...preferences };
        this.preferencesSubject.next(combinedPrefs);
        
        // Try to migrate to backend storage
        this.migratePreferencesToBackend(preferences);
      } else {
        // No data found, start with defaults
        this.preferencesSubject.next(this.getDefaultPreferences());
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      this.preferencesSubject.next(this.getDefaultPreferences());
    }
    
    this.initialized = true;
  }

  private async migratePreferencesToBackend(preferences: any): Promise<void> {
    try {
      // Map old preference format to new format
      const mappedPrefs: Partial<UserPreferences> = {};
      
      if (preferences.voiceEnabled !== undefined) {
        mappedPrefs.voiceControlsEnabled = preferences.voiceEnabled;
      }
      if (preferences.playbackDevice !== undefined) {
        mappedPrefs.defaultPlaybackDevice = preferences.playbackDevice;
      }
      
      await this.backendStorage.updateUserPreferences(mappedPrefs).toPromise();
      
      // Clear localStorage after successful migration
      localStorage.removeItem('userPreferences');
      console.log('Successfully migrated user preferences to backend');
    } catch (error) {
      console.warn('User preferences migration to backend failed:', error);
    }
  }

  /**
   * Save preferences to localStorage (fallback)
   */
  private savePreferencesToLocalStorage(preferences: UserPreferences): void {
    try {
      localStorage.setItem('userPreferences', JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving preferences to localStorage:', error);
    }
  }

  /**
   * Get default user preferences
   */
  private getDefaultPreferences(): UserPreferences {
    return {
      autoRemoveDuplicates: false,
      voiceControlsEnabled: false,
      defaultPlaybackDevice: undefined
    };
  }
}